import {
  Accordion,
  Alert,
  Badge,
  Button,
  Center,
  Code,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { DocumentReference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import type { NotificationTypeDefinition } from '@polarissleep/shared';
import { ALL_NOTIFICATIONS } from '@polarissleep/shared';
import { IconAlertCircle, IconArrowLeft, IconDeviceFloppy } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { TEMPLATE_TYPE_SYSTEM } from '../../../lib/template-renderers/constants';
import { PageHeader } from '../../components/layout/PageHeader';
import { getOrganizationId } from '../../utils/tenant';
import {
  buildDocumentReferenceForChannel,
  getDocRefPath,
  parseTemplatePath,
  TEMPLATE_CHANNEL_LABELS,
  type TemplateChannel,
  templateChannelsForNotification,
} from './notification-template-helpers';

const CHANNEL_BADGE_COLORS: Record<TemplateChannel, string> = {
  'email-subject': 'cyan',
  'email-body': 'blue',
  'in-app-body': 'orange',
  'sms-body': 'green',
};

interface ChannelState {
  docRef: DocumentReference | null;
  content: string;
  dirty: boolean;
}

type ChannelStates = Partial<Record<TemplateChannel, ChannelState>>;

export function NotificationEditorByKey() {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const { notificationKey = '' } = useParams<{ notificationKey: string }>();

  const def = useMemo<NotificationTypeDefinition | null>(
    () => ALL_NOTIFICATIONS[notificationKey as keyof typeof ALL_NOTIFICATIONS] ?? null,
    [notificationKey]
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [channelStates, setChannelStates] = useState<ChannelStates>({});
  const [envOrgId, setEnvOrgId] = useState<string | null>(null);

  const env = String(import.meta.env.VITE_POLARIS_ENV ?? '');

  const fetchTemplates = useCallback(async () => {
    if (!def) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const allDocRefs = await medplum.searchResources('DocumentReference', {
        _tag: `${TEMPLATE_TYPE_SYSTEM}|notification-template`,
        _count: '200',
      });

      const forThisKey = allDocRefs.filter((docRef) => {
        const path = getDocRefPath(docRef);
        if (!path) {
          return false;
        }
        return parseTemplatePath(path)?.notificationKey === def.key;
      });

      // Env Organization id is build-time-injected into VITE_MEDPLUM_ORGANIZATION_ID
      // by the preview-env / deploy workflows; same value every tenant-scoped
      // write in the app uses via withTenantScope. Reading it directly avoids
      // a round-trip + sidesteps AccessPolicy read constraints on Organization,
      // and works on the very first save in an env (no prior template to lift
      // meta.account from).
      setEnvOrgId(getOrganizationId() ?? null);

      const states: ChannelStates = {};
      for (const channel of templateChannelsForNotification(def)) {
        const docRef = forThisKey.find((d) => d.category?.[0]?.coding?.[0]?.code === channel) ?? null;
        const data = docRef?.content?.[0]?.attachment?.data;
        states[channel] = {
          docRef,
          content: data ? atob(data) : '',
          dirty: false,
        };
      }
      setChannelStates(states);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load templates for this notification',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [medplum, def]);

  useEffect(() => {
    fetchTemplates().catch(console.error);
  }, [fetchTemplates]);

  const setChannelContent = (channel: TemplateChannel, content: string) => {
    setChannelStates((prev) => {
      const existing = prev[channel];
      if (!existing) {
        return prev;
      }
      return {
        ...prev,
        [channel]: {
          ...existing,
          content,
          dirty: true,
        },
      };
    });
  };

  const anyDirty = Object.values(channelStates).some((s) => s?.dirty);

  const handleSave = async () => {
    if (!def) {
      return;
    }
    setSaving(true);
    const failures: { channel: TemplateChannel; error: unknown }[] = [];
    const nextStates: ChannelStates = { ...channelStates };

    for (const [channelStr, state] of Object.entries(channelStates)) {
      const channel = channelStr as TemplateChannel;
      if (!state?.dirty) {
        continue;
      }

      try {
        if (state.docRef) {
          const attachment = state.docRef.content?.[0]?.attachment;
          const updated: DocumentReference = {
            ...state.docRef,
            content: [
              {
                attachment: {
                  contentType: attachment?.contentType ?? 'text/plain',
                  data: btoa(state.content),
                  title: attachment?.title ?? state.docRef.type?.text ?? channel,
                },
              },
            ],
          };
          const result = await medplum.updateResource(updated);
          nextStates[channel] = { docRef: result, content: state.content, dirty: false };
        } else {
          if (!envOrgId) {
            throw new Error(
              'VITE_MEDPLUM_ORGANIZATION_ID is not set in this build — tenant scoping is broken. Check the env-provisioning workflow.'
            );
          }
          const newDoc = buildDocumentReferenceForChannel({
            notificationKey: def.key,
            channel,
            content: state.content,
            env,
            envOrgId,
            notificationLabel: def.label,
          });
          const result = await medplum.createResource(newDoc);
          nextStates[channel] = { docRef: result, content: state.content, dirty: false };
        }
      } catch (error) {
        console.error(`Failed to save ${channel}:`, error);
        failures.push({ channel, error });
      }
    }

    setChannelStates(nextStates);
    setSaving(false);

    if (failures.length === 0) {
      notifications.show({
        title: 'Saved',
        message: 'Templates updated successfully',
        color: 'green',
      });
    } else {
      const failedLabels = failures.map((f) => TEMPLATE_CHANNEL_LABELS[f.channel]).join(', ');
      notifications.show({
        title: 'Partial save',
        message: `Failed to save: ${failedLabels}. See console for details.`,
        color: 'red',
      });
    }
  };

  if (loading) {
    return (
      <Stack gap="md" p="md">
        <PageHeader title="Loading…" />
        <Paper withBorder radius="md" shadow="sm" p="md">
          <Center py="xl">
            <Loader />
          </Center>
        </Paper>
      </Stack>
    );
  }

  if (!def) {
    return (
      <Stack gap="md" p="md">
        <PageHeader
          title="Unknown notification"
          actions={
            <Button
              variant="subtle"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => navigate('/admin/notification-templates')?.catch(console.error)}
            >
              Back to Templates
            </Button>
          }
        />
        <Paper withBorder radius="md" shadow="sm" p="md">
          <Text>Unknown notification key: {notificationKey}</Text>
        </Paper>
      </Stack>
    );
  }

  const channels = templateChannelsForNotification(def);

  return (
    <Stack gap="md" p="md">
      <PageHeader
        title={def.label}
        actions={
          <Group gap="sm">
            <Button
              variant="subtle"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => navigate('/admin/notification-templates')?.catch(console.error)}
            >
              Back to Templates
            </Button>
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={handleSave}
              loading={saving}
              disabled={!anyDirty}
            >
              Save changes
            </Button>
          </Group>
        }
      />

      <Paper withBorder radius="md" shadow="sm" p="md">
        <Stack gap="xs">
          <Group gap="xs">
            <Code>{def.key}</Code>
            <Badge size="sm" variant="outline" color="gray">
              {def.audience.toLowerCase()}
            </Badge>
            <Badge size="sm" variant="outline" color="gray">
              {def.category.toLowerCase()}
            </Badge>
          </Group>
          <Text size="sm" c="dimmed">
            {def.description}
          </Text>
        </Stack>
      </Paper>

      <Paper withBorder radius="md" shadow="sm" p="md">
        <Stack gap="md">
          <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light" title="Template Syntax">
            Templates use Pug syntax. Variables are referenced with <Code>{'#{variableName}'}</Code>. Changes take
            effect on the next notification sent.
          </Alert>

          <Accordion variant="contained">
            <Accordion.Item value="variables">
              <Accordion.Control>Available template variables</Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" c="dimmed">
                  Variables available in each template depend on the notification's context generator (see{' '}
                  <Code>apps/provider-app/lib/context-generators</Code>). Common: <Code>{'#{firstName}'}</Code>,{' '}
                  <Code>{'#{patientName}'}</Code>, <Code>{'#{appointmentDate}'}</Code>,{' '}
                  <Code>{'#{appointmentType}'}</Code>, <Code>{'#{senderName}'}</Code>,{' '}
                  <Code>{'#{messageContent}'}</Code>, <Code>{'#{messageTopic}'}</Code>.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Stack>
      </Paper>

      {channels.map((channel) => {
        const state = channelStates[channel];
        if (!state) {
          return null;
        }
        const isMissing = state.docRef === null;
        return (
          <Paper key={channel} withBorder radius="md" shadow="sm" p="md">
            <Stack gap="xs">
              <Group justify="space-between">
                <Group gap="xs">
                  <Badge color={CHANNEL_BADGE_COLORS[channel]} variant="light">
                    {channel}
                  </Badge>
                  <Text fw={600}>{TEMPLATE_CHANNEL_LABELS[channel]}</Text>
                </Group>
                {isMissing && (
                  <Badge color="gray" variant="outline">
                    Not yet configured
                  </Badge>
                )}
                {state.dirty && (
                  <Badge color="yellow" variant="light">
                    Unsaved
                  </Badge>
                )}
              </Group>
              <Textarea
                value={state.content}
                onChange={(e) => setChannelContent(channel, e.currentTarget.value)}
                minRows={channel === 'email-subject' ? 2 : 10}
                maxRows={channel === 'email-subject' ? 4 : 30}
                autosize
                placeholder={
                  isMissing
                    ? `No ${TEMPLATE_CHANNEL_LABELS[channel]} template yet — typing content here will create one on Save.`
                    : undefined
                }
                styles={{
                  input: {
                    fontFamily: 'monospace',
                    fontSize: '13px',
                  },
                }}
              />
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}
